import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import api from "../../utils/api";
import { API_ENDPOINTS } from "../../constants/api";
import { setAccessToken } from "../../utils/tokenUtils";
import { fontFamily } from "@/styles/font";
import logger from "@/utils/logger";

// 성별 타입 정의
type Gender = "MALE" | "FEMALE" | "OTHER";

// 생년월일 인터페이스
interface BirthDate {
    year: string;
    month: string;
    day: string;
}

// 회원가입 응답 인터페이스
interface SignUpResponse {
    success: boolean;
    message: string;
    data: {
        accessToken: string;
        refreshToken: string;
        temporaryToken: string;
        isRegistered: boolean;
    };
    errorCode?: string;
}

// 폼 에러 인터페이스
interface FormErrors {
    genderError: string;
    birthDateError: string;
    alcoholTypeError: string;
}

// 약관 동의 상태 인터페이스
interface AgreementState {
    all: boolean;
    service: boolean;
    privacy: boolean;
    collection: boolean;
    location: boolean;
    marketing: boolean;
}

const UserInfoForm: React.FC = () => {
    const navigate = useNavigate();
    const [nickname, setNickname] = useState<string>("");
    const isLoading = useRef<boolean>(false);
    const retryCount = useRef<number>(0);
    const MAX_RETRY_COUNT = 2;

    // 약관 동의 상태
    const [agreements, setAgreements] = useState<AgreementState>({
        all: false,
        service: false,
        privacy: false,
        collection: false,
        location: false,
        marketing: false
    });

    // 성별 상태
    const [selectedGender, setSelectedGender] = useState<Gender | null>(null);

    // 생년월일 상태
    const [birthDate, setBirthDate] = useState<BirthDate>({
        year: "",
        month: "",
        day: ""
    });

    // 주종 상태
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

    // 폼 에러 상태 통합 관리
    const [formErrors, setFormErrors] = useState<FormErrors>({
        genderError: "",
        birthDateError: "",
        alcoholTypeError: ""
    });

    // 컴포넌트 마운트 시 세션 스토리지에서 저장된 데이터 불러오기
    useEffect(() => {
        // 닉네임 불러오기
        const savedNickname = sessionStorage.getItem("nickname");
        if (savedNickname) {
            setNickname(savedNickname);
        }

        // 약관 동의 정보 불러오기
        try {
            const savedAgreements = sessionStorage.getItem("agreements");
            if (savedAgreements) {
                const parsedAgreements = JSON.parse(savedAgreements) as AgreementState;
                setAgreements(parsedAgreements);
            }
        } catch (error) {
            logger.error("약관 동의 정보 파싱 오류:", error);
        }

        // 성별 정보 불러오기
        const savedGender = sessionStorage.getItem("selectedGender") as Gender | null;
        if (savedGender) {
            setSelectedGender(savedGender);
        }

        // 생년월일 정보 불러오기
        const savedBirthDate = sessionStorage.getItem("birthDate");
        if (savedBirthDate) {
            try {
                const parsedBirthDate = JSON.parse(savedBirthDate) as BirthDate;
                setBirthDate(parsedBirthDate);
            } catch (error) {
                logger.error("생년월일 정보 파싱 오류:", error);
            }
        }

        // 주종 정보 불러오기
        const savedTypes = sessionStorage.getItem("selectedTypes");
        if (savedTypes) {
            try {
                const parsedTypes = JSON.parse(savedTypes) as string[];
                setSelectedTypes(parsedTypes);
            } catch (error) {
                logger.error("주종 정보 파싱 오류:", error);
            }
        }
    }, []);

    // 성별 옵션
    const genderOptions: { value: Gender; label: string }[] = [
        { value: "FEMALE", label: "여성" },
        { value: "MALE", label: "남성" },
        { value: "OTHER", label: "선택안함" }
    ];

    // 주종 옵션
    const alcoholTypes = [
        { id: "SOJU", label: "증류주 (소주)" },
        { id: "BEER", label: "맥주" },
        { id: "FRUIT_WINE", label: "와인 (과실주)" },
        { id: "TAKJU", label: "탁주 (막걸리)" },
        { id: "YAKJU_CHEONGJU", label: "청, 약주" },
        { id: "LIQUEUR", label: "리큐르" },
        { id: "WHISKY", label: "위스키" },
        { id: "BRANDY", label: "브랜디" },
        { id: "ETC", label: "기타주류" },
        { id: "NONE", label: "아직없음" }
    ];

    // 생년월일 관련 데이터
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => String(currentYear - i));
    const months = Array.from({ length: 12 }, (_, i) => String(i + 1));

    const getDaysInMonth = (year: string, month: string) => {
        if (!year || !month) return 31;
        return new Date(parseInt(year), parseInt(month), 0).getDate();
    };

    const days = Array.from({ length: getDaysInMonth(birthDate.year, birthDate.month) }, (_, i) => String(i + 1));

    // 14세 이상인지 확인
    const isOver14 = (birthDate: BirthDate): boolean => {
        if (!birthDate.year || !birthDate.month || !birthDate.day) return false;

        const birthDateTime = new Date(
            parseInt(birthDate.year),
            parseInt(birthDate.month) - 1,
            parseInt(birthDate.day)
        ).getTime();

        const minAgeDate = new Date();
        minAgeDate.setFullYear(minAgeDate.getFullYear() - 14);

        return birthDateTime <= minAgeDate.getTime();
    };

    // 폼 유효성 검사 및 버튼 활성화 상태 계산
    const isValid =
        selectedGender !== null &&
        birthDate.year !== "" &&
        birthDate.month !== "" &&
        birthDate.day !== "" &&
        isOver14(birthDate) &&
        selectedTypes.length > 0 &&
        !isLoading.current;

    // 생년월일 변경 시 오류 메시지 초기화 로직
    useEffect(() => {
        // 생년월일이 입력되었을 때 14세 이상인지 확인
        const isBirthDateFilled = birthDate.year !== "" && birthDate.month !== "" && birthDate.day !== "";

        if (isBirthDateFilled) {
            const isBirthDateValid = isOver14(birthDate);
            // 14세 미만인 경우 오류 메시지 표시
            if (!isBirthDateValid) {
                setFormErrors((prev) => ({
                    ...prev,
                    birthDateError: "14세 미만은 가입할 수 없습니다"
                }));
            } else {
                setFormErrors((prev) => ({
                    ...prev,
                    birthDateError: ""
                }));
            }
        }
    }, [birthDate]);

    // 성별 선택 핸들러
    const handleGenderSelect = (gender: Gender) => {
        setSelectedGender(gender);
        setFormErrors((prev) => ({
            ...prev,
            genderError: ""
        }));
    };

    // 생년월일 핸들러 통합
    const handleBirthDateChange = (type: string, e: React.ChangeEvent<HTMLSelectElement>) => {
        setBirthDate((prev) => ({
            ...prev,
            [type]: e.target.value
        }));

        // 생년월일 변경 시 오류 메시지 초기화 (14세 검증은 useEffect에서 수행)
        if (formErrors.birthDateError && formErrors.birthDateError !== "14세 미만은 가입할 수 없습니다") {
            setFormErrors((prev) => ({
                ...prev,
                birthDateError: ""
            }));
        }
    };

    // 주종 선택 핸들러 개선
    const handleTypeClick = (typeId: string) => {
        setSelectedTypes((prev) => {
            // 이미 선택된 경우 제거
            if (prev.includes(typeId)) {
                return prev.filter((id) => id !== typeId);
            }

            // 최대 3개까지만 선택 가능
            if (prev.length >= 3) {
                return prev;
            }

            // 새로운 타입 추가
            return [...prev, typeId];
        });

        setFormErrors((prev) => ({
            ...prev,
            alcoholTypeError: ""
        }));
    };

    const handleBack = () => {
        // 현재 입력 데이터를 세션 스토리지에 저장
        if (nickname) {
            sessionStorage.setItem("nickname", nickname);
        }

        // 성별 정보 저장 (선택된 경우에만)
        if (selectedGender) {
            sessionStorage.setItem("selectedGender", selectedGender);
        }

        // 생년월일 정보 저장 (입력된 경우에만)
        if (birthDate.year || birthDate.month || birthDate.day) {
            sessionStorage.setItem("birthDate", JSON.stringify(birthDate));
        }

        // 주종 정보 저장 (선택된 경우에만)
        if (selectedTypes.length > 0) {
            sessionStorage.setItem("selectedTypes", JSON.stringify(selectedTypes));
        }

        navigate(-1);
    };

    // 회원가입 API 요청 함수 최적화
    const signUp = async (): Promise<boolean> => {
        try {
            // 임시 토큰 가져오기
            const temporaryToken = localStorage.getItem("temporaryToken");

            // temporaryToken이 없는 경우 오류 처리
            if (!temporaryToken) {
                logger.error("임시 토큰이 없습니다. 정상적인 로그인 프로세스가 아닙니다.");
                clearJoinData();
                navigate("/", { replace: true });
                return false;
            }

            // 필수 데이터 검증
            if (!nickname) {
                logger.error("닉네임이 설정되지 않았습니다.");
                clearJoinData();
                navigate("/", { replace: true });
                return false;
            }

            if (!selectedGender) {
                logger.error("성별이 선택되지 않았습니다.");
                clearJoinData();
                navigate("/", { replace: true });
                return false;
            }

            if (!birthDate.year || !birthDate.month || !birthDate.day) {
                logger.error("생년월일이 모두 선택되지 않았습니다.");
                clearJoinData();
                navigate("/", { replace: true });
                return false;
            }

            if (!isOver14(birthDate)) {
                logger.error("14세 미만은 가입할 수 없습니다.");
                clearJoinData();
                navigate("/", { replace: true });
                return false;
            }

            if (selectedTypes.length === 0) {
                logger.error("최소 1개 이상의 주종을 선택해야 합니다.");
                clearJoinData();
                navigate("/", { replace: true });
                return false;
            }

            // 생년월일 포맷팅
            const formattedMonth = birthDate.month.padStart(2, "0");
            const formattedDay = birthDate.day.padStart(2, "0");
            const formattedBirthday = `${birthDate.year}-${formattedMonth}-${formattedDay}`;

            // 회원가입 요청 데이터
            const signUpData = {
                temporaryToken,
                nickname,
                gender: selectedGender,
                birthday: formattedBirthday,
                alcoholPreferenceList: selectedTypes,
                isMarketingConsent: agreements.marketing
            };
            // 회원가입 API 요청
            const response = await api.post<SignUpResponse>(API_ENDPOINTS.SNS_SIGNUP, signUpData);

            // 응답 처리
            if (response.data.success) {
                // 액세스 토큰 저장
                setAccessToken(response.data.data.accessToken);

                // 회원가입 관련 모든 데이터 정리
                clearJoinData();

                // 홈으로 이동
                navigate("/", { replace: true });
                return true;
            } else {
                logger.error("회원가입 실패:", response.data.message || "회원가입에 실패했습니다.");
                clearJoinData();
                navigate("/", { replace: true });
                return false;
            }
        } catch (error) {
            logger.error("회원가입 오류:", error);
            clearJoinData();
            navigate("/", { replace: true });
            return false;
        }
    };

    // 회원가입 관련 데이터 정리 함수
    const clearJoinData = () => {
        // 세션 스토리지에서 회원가입 관련 데이터 삭제
        sessionStorage.removeItem("nickname");
        sessionStorage.removeItem("selectedGender");
        sessionStorage.removeItem("birthDate");
        sessionStorage.removeItem("selectedTypes");
        sessionStorage.removeItem("agreements");

        // 로컬 스토리지에서 임시 토큰 삭제
        localStorage.removeItem("temporaryToken");
    };

    // 가입 완료 핸들러 최적화
    const handleComplete = async () => {
        // 로딩 상태 설정
        if (isLoading.current) {
            return; // 이미 처리 중이면 중복 실행 방지
        }

        // 닉네임 검증
        if (!nickname) {
            logger.error("닉네임이 설정되지 않았습니다.");
            clearJoinData();
            navigate("/", { replace: true });
            return;
        }

        // 임시 토큰 검증
        const temporaryToken = localStorage.getItem("temporaryToken");
        if (!temporaryToken) {
            logger.error("임시 토큰이 없습니다. 정상적인 로그인 프로세스가 아닙니다.");
            clearJoinData();
            navigate("/", { replace: true });
            return;
        }

        // 폼 유효성 검사
        let isFormValid = true;

        // 성별 검증
        if (!selectedGender) {
            setFormErrors((prev) => ({
                ...prev,
                genderError: "성별을 선택해주세요"
            }));
            isFormValid = false;
        }

        // 생년월일 검증
        if (!birthDate.year || !birthDate.month || !birthDate.day) {
            setFormErrors((prev) => ({
                ...prev,
                birthDateError: "생년월일을 모두 선택해주세요"
            }));
            isFormValid = false;
        } else if (!isOver14(birthDate)) {
            setFormErrors((prev) => ({
                ...prev,
                birthDateError: "14세 미만은 가입할 수 없습니다"
            }));
            isFormValid = false;
        }

        // 주종 검증
        if (selectedTypes.length === 0) {
            setFormErrors((prev) => ({
                ...prev,
                alcoholTypeError: "최소 1개 이상의 주종을 선택해주세요"
            }));
            isFormValid = false;
        }

        if (isFormValid) {
            isLoading.current = true;

            try {
                // 최대 재시도 횟수만큼 반복
                for (let attempt = 0; attempt <= MAX_RETRY_COUNT; attempt++) {
                    retryCount.current = attempt;
                    logger.log(`회원가입 시도 ${attempt + 1}/${MAX_RETRY_COUNT + 1}`);

                    try {
                        const success = await signUp();
                        if (success) {
                            return; // 성공하면 종료
                        }

                        // 실패했지만 재시도 가능한 경우 잠시 대기 후 재시도
                        if (attempt < MAX_RETRY_COUNT) {
                            await new Promise((resolve) => setTimeout(resolve, 500));
                        } else {
                            // 최대 재시도 횟수 도달
                            logger.error("최대 재시도 횟수에 도달했습니다. 회원가입에 실패했습니다.");
                            clearJoinData();
                            navigate("/", { replace: true });
                        }
                    } catch (error) {
                        logger.error(`회원가입 오류 (시도 ${attempt + 1}/${MAX_RETRY_COUNT + 1}):`, error);

                        // 최대 재시도 횟수 도달
                        if (attempt === MAX_RETRY_COUNT) {
                            logger.error("최대 재시도 횟수에 도달했습니다. 회원가입에 실패했습니다.");
                            clearJoinData();
                            navigate("/", { replace: true });
                        } else {
                            // 재시도 가능한 경우 잠시 대기
                            await new Promise((resolve) => setTimeout(resolve, 500));
                        }
                    }
                }
            } finally {
                isLoading.current = false;
            }
        }
    };

    return (
        <Container>
            {/* 네비게이션 바 */}
            <NavigationBar>
                <TextActionFrame>
                    <IconButton onClick={handleBack}>
                        <BackIcon />
                    </IconButton>
                </TextActionFrame>
                <LargeTextContainer>
                    <Title>{nickname || "술호랑"}님에 대해 알려주세요</Title>
                    <SubTitle>필수항목을 입력하고 나에게 맞는 전통주를 추천받아요.</SubTitle>
                </LargeTextContainer>
            </NavigationBar>

            {/* 콘텐츠 영역 */}
            <ContentSection>
                {/* 성별 섹션 */}
                <FormCard>
                    <SectionTitle>성별</SectionTitle>
                    {formErrors.genderError && <ErrorMessage>{formErrors.genderError}</ErrorMessage>}

                    <GenderOptionsContainer>
                        {genderOptions.map((option) => (
                            <RadioButtonContainer key={option.value}>
                                <RadioButton
                                    isSelected={selectedGender === option.value}
                                    onClick={() => handleGenderSelect(option.value)}
                                >
                                    {selectedGender === option.value && <RadioButtonActive />}
                                </RadioButton>
                                <RadioLabel>{option.label}</RadioLabel>
                            </RadioButtonContainer>
                        ))}
                    </GenderOptionsContainer>
                </FormCard>

                {/* 생년월일 섹션 */}
                <FormCard>
                    <SectionTitle>생년월일</SectionTitle>
                    {formErrors.birthDateError && <ErrorMessage>{formErrors.birthDateError}</ErrorMessage>}

                    <DateSelectContainer>
                        <YearSelect value={birthDate.year} onChange={(e) => handleBirthDateChange("year", e)}>
                            <option value="" disabled>
                                년도
                            </option>
                            {years.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </YearSelect>
                        <MonthSelect value={birthDate.month} onChange={(e) => handleBirthDateChange("month", e)}>
                            <option value="" disabled>
                                월
                            </option>
                            {months.map((month) => (
                                <option key={month} value={month}>
                                    {month}
                                </option>
                            ))}
                        </MonthSelect>
                        <DaySelect value={birthDate.day} onChange={(e) => handleBirthDateChange("day", e)}>
                            <option value="" disabled>
                                일
                            </option>
                            {days.map((day) => (
                                <option key={day} value={day}>
                                    {day}
                                </option>
                            ))}
                        </DaySelect>
                    </DateSelectContainer>
                </FormCard>

                {/* 주종 섹션 */}
                <FormCard>
                    <AlcoholTypeTitle>
                        자주 마시는 주종은 무엇인가요? <AlcoholTypeSubtitle>(최대 3개)</AlcoholTypeSubtitle>
                    </AlcoholTypeTitle>
                    {formErrors.alcoholTypeError && <ErrorMessage>{formErrors.alcoholTypeError}</ErrorMessage>}

                    <AlcoholTypesContainer>
                        <AlcoholTypeRow>
                            {alcoholTypes.slice(0, 3).map((type) => (
                                <AlcoholTypeButton
                                    key={type.id}
                                    isSelected={selectedTypes.includes(type.id)}
                                    onClick={() => handleTypeClick(type.id)}
                                >
                                    {type.label}
                                </AlcoholTypeButton>
                            ))}
                        </AlcoholTypeRow>
                        <AlcoholTypeRow>
                            {alcoholTypes.slice(3, 6).map((type) => (
                                <AlcoholTypeButton
                                    key={type.id}
                                    isSelected={selectedTypes.includes(type.id)}
                                    onClick={() => handleTypeClick(type.id)}
                                >
                                    {type.label}
                                </AlcoholTypeButton>
                            ))}
                        </AlcoholTypeRow>
                        <AlcoholTypeRow>
                            {alcoholTypes.slice(6, 10).map((type) => (
                                <AlcoholTypeButton
                                    key={type.id}
                                    isSelected={selectedTypes.includes(type.id)}
                                    onClick={() => handleTypeClick(type.id)}
                                >
                                    {type.label}
                                </AlcoholTypeButton>
                            ))}
                        </AlcoholTypeRow>
                    </AlcoholTypesContainer>
                </FormCard>
            </ContentSection>

            {/* 가입 완료 버튼 */}
            <ButtonWrapper>
                <CompleteButton onClick={handleComplete} disabled={!isValid} isEnabled={isValid}>
                    {isLoading.current ? "처리 중..." : "가입 완료"}
                </CompleteButton>
            </ButtonWrapper>
        </Container>
    );
};

// 스타일 컴포넌트
const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    width: 100%;
    min-width: 320px;
    max-width: 430px;
    height: calc(100vh - 40px);
    margin: 0 auto;
    background: #ffffff;
    font-family: ${fontFamily.base};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-size: clamp(14px, 2.8vw, 18px);
    line-height: 1.5;
    font-weight: 400;
    overflow-x: hidden;
`;

const NavigationBar = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    width: 100%;
    min-height: 110px;
    height: auto;
    background: #ffffff;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

const TextActionFrame = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0px;
    gap: 262px;
    width: 100%;
    height: 48px;
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
`;

const LargeTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 16px;
    gap: 8px;
    width: 100%;
    min-height: 62px;
    height: auto;
    background: #ffffff;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

const Title = styled.h2`
    width: 100%;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    color: #1f1f1f;
    margin: 0;
    word-break: break-word;
`;

const IconButton = styled.button`
    width: 48px;
    height: 48px;
    background: transparent;
    border: none;
    cursor: pointer;
    position: relative;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const BackIcon = styled.div`
    position: absolute;
    width: 24px;
    height: 24px;
    left: calc(50% - 24px / 2);
    top: calc(50% - 24px / 2);
    background-image: url("/icon/chevron-left.png");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
`;

const ContentSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0 16px;
    width: 100%;
    height: calc(100vh - 158px - var(--extra-height, 0px));
    flex: none;
    order: 2;
    align-self: stretch;
    flex-grow: 1;
    box-sizing: border-box;
    overflow-y: auto;
    position: relative;
    padding-top: 8px;
    overflow-y: auto; /* 내용이 넘칠 경우만 스크롤 */
`;

const SubTitle = styled.p`
    width: 100%;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: clamp(12px, 2.4vw, 16px);
    line-height: 1.5;
    color: #a9b5c0;
    margin: 0;
    word-break: break-word;
`;

const FormCard = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 16px;
    background: #ffffff;
    border: 1px solid #e8eef2;
    border-radius: 12px;
    margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 125%;
    text-align: center;
    color: #1f1f1f;
    margin: 0 0 8px 0;
`;

const ErrorMessage = styled.div`
    width: 100%;
    height: 20px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    text-align: center;
    color: #d32f2f;
`;

const GenderOptionsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0px;
    gap: 25px;
    width: 100%;
    margin: 8px 0;
`;

const RadioButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0px;
    gap: 4px;
    height: 24px;
    flex: none;
    flex-grow: 0;
`;

interface RadioButtonProps {
    isSelected: boolean;
}

const RadioButton = styled.div<RadioButtonProps>`
    box-sizing: border-box;
    width: 24px;
    height: 24px;
    background: ${(props) => (props.isSelected ? "#F2F8FC" : "#F2F8FC")};
    border: 1px solid ${(props) => (props.isSelected ? "#007F73" : "rgba(0, 0, 0, 0.1)")};
    border-radius: 16px;
    flex: none;
    order: 0;
    flex-grow: 0;
    position: relative;
    cursor: pointer;
`;

const RadioButtonActive = styled.div`
    box-sizing: border-box;
    position: absolute;
    left: 16.67%;
    right: 16.67%;
    top: 16.67%;
    bottom: 16.67%;
    background: #007f73;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 16px;
`;

const RadioLabel = styled.div`
    height: 24px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 24px;
    color: #17191a;
    flex: none;
    order: 1;
    flex-grow: 0;
`;

const Divider = styled.hr`
    width: 100%;
    height: 1px;
    background: #e8eef2;
    border: none;
    margin: 12px 0;
`;

const DateSelectContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 15px;
    width: 100%;
    margin: 8px 0;
`;

const SelectBase = styled.select`
    box-sizing: border-box;
    height: 40px;
    background: #ffffff;
    border: 1px solid #c0c0c0;
    border-radius: 10px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 19px;
    color: #17191a;
    padding: 0 10px;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #007f73;
    }
`;

const YearSelect = styled(SelectBase)`
    flex: 2;
`;

const MonthSelect = styled(SelectBase)`
    flex: 1;
`;

const DaySelect = styled(SelectBase)`
    flex: 1;
`;

const AlcoholTypeTitle = styled.h2`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 125%;
    text-align: center;
    color: #1f1f1f;
    margin: 0 0 8px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`;

const AlcoholTypeSubtitle = styled.span`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 350;
    font-size: 12px;
    line-height: 14px;
    color: #414141;
    margin-left: 5px;
`;

const AlcoholTypesContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0px;
    gap: 8px;
    width: 100%;
    margin: 8px 0;
`;

const AlcoholTypeRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0px;
    gap: 10px;
    width: 100%;
    flex: none;
    align-self: stretch;
    flex-grow: 0;
`;

interface AlcoholTypeButtonProps {
    isSelected: boolean;
}

const AlcoholTypeButton = styled.button<AlcoholTypeButtonProps>`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 10px;
    height: 43px;
    background: ${(props) => (props.isSelected ? "#007F73" : "#FFFFFF")};
    border: 1px solid ${(props) => (props.isSelected ? "#007F73" : "#C0C0C0")};
    border-radius: 10px;
    flex: 1;
    cursor: pointer;

    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 19px;
    color: ${(props) => (props.isSelected ? "#FFFFFF" : "#5F5F5F")};
`;

const ButtonWrapper = styled.div`
    width: 100%;
    position: fixed;
    bottom: 20px;
    left: 0;
    right: 0;
    padding: 0 16px;
    max-width: 430px;
    margin: 0 auto;
    z-index: 10;
`;

interface CompleteButtonProps {
    isEnabled: boolean;
}

const CompleteButton = styled.button<CompleteButtonProps>`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 16px;
    gap: 8px;
    width: 100%;
    height: 56px;
    background: ${(props) => (props.isEnabled ? "#007F73" : "#CCCCCC")};
    border-radius: 12px;
    border: none;
    cursor: ${(props) => (props.isEnabled ? "pointer" : "default")};

    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    color: #ffffff;
    opacity: ${(props) => (props.isEnabled ? "1" : "0.7")};
`;

export default UserInfoForm;
